import { useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * The frame every desk action on a forex order opens in: a title, the fields,
 * and Close and Save. When core refuses, its sentence is shown inside the
 * dialog, above the fields so it is never below the fold on a phone, and the dialog stays open with what
 * they typed.
 */
export default function DeskDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  onSubmit,
  submitLabel = "Save",
  busy = false,
  submitDisabled = false,
  hideSubmit = false,
  closeLabel = "Cancel",
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  onSubmit: () => void;
  submitLabel?: string;
  busy?: boolean;
  submitDisabled?: boolean;
  /** For a dialog that has already done its job and only shows the result. */
  hideSubmit?: boolean;
  closeLabel?: string;
  error?: string | null;
}) {
  // The refusal is the first thing in the dialog; bring it into view if the person had scrolled down.
  const alertRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (error) alertRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [error]);
  // Focus goes back to whatever opened the dialog (a dialog with no Radix trigger would otherwise drop it on the page).
  const opener = useRef<HTMLElement | null>(null);
  const openerLabel = useRef("");
  const wasOpen = useRef(false);
  if (open && !wasOpen.current) {
    opener.current = document.activeElement as HTMLElement | null;
    openerLabel.current = opener.current?.getAttribute("aria-label") || opener.current?.textContent?.trim() || "";
  }
  wasOpen.current = open;
  const focusBack = () => {
    let back = opener.current;
    // The page may have redrawn the button while the dialog was open: find its twin by name.
    if (back && !document.contains(back) && openerLabel.current) {
      back = [...document.querySelectorAll<HTMLElement>("button")].find((b) => (b.getAttribute("aria-label") || b.textContent?.trim()) === openerLabel.current) ?? null;
    }
    if (back && document.contains(back)) back.focus();
  };
  // A dialog that is taken off the page while open (its screen redrew) still gives focus back.
  const focusBackRef = useRef(focusBack);
  focusBackRef.current = focusBack;
  useEffect(
    () => () => {
      if (wasOpen.current) setTimeout(() => focusBackRef.current(), 50);
    },
    [],
  );
  // An old toast is not left on screen over a new dialog.
  useEffect(() => {
    if (!open) return;
    // Success and information toasts go; a warning or an error stays until it times out or is closed.
    for (const t of toast.getHistory() as { id: string | number; type?: string; dismiss?: boolean }[]) {
      if (!t.dismiss && (t.type === "success" || t.type === "info" || t.type === undefined || t.type === "default")) toast.dismiss(t.id);
    }
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          focusBack();
        }}
        className="flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-md flex-col gap-0 overflow-hidden rounded-xl bg-white-A700 p-0">
        <DialogHeader className="shrink-0 border-b border-[#D2D2D2] px-6 pb-3 pt-6 text-left">
          <DialogTitle className="text-lg font-bold text-[#333333]">{title}</DialogTitle>
          {description ? (
            <DialogDescription className="text-sm text-[#666666]">{description}</DialogDescription>
          ) : (
            <DialogDescription className="sr-only">{title}</DialogDescription>
          )}
        </DialogHeader>
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            if (!busy && !submitDisabled) onSubmit();
          }}
        >
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
            {error ? (
              <p ref={alertRef} role="alert" className="rounded-lg border border-[#E8B4B4] bg-[#FFF3F3] px-3 py-2 text-sm text-[#7A1F1F]">
                {error}
              </p>
            ) : null}
            {children}
          </div>
          <div className="flex shrink-0 flex-row justify-end gap-3 rounded-b-xl bg-[#F9F9F9] px-6 py-4">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={busy}
              className="cursor-pointer rounded-lg border border-[#165FE3] px-4 py-2.5 font-medium text-[#165FE3] transition-colors hover:bg-[#F0F4FF] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#165FE3] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {closeLabel}
            </button>
            {hideSubmit ? null : (
            <button
              type="submit"
              disabled={busy || submitDisabled}
              className="cursor-pointer rounded-lg bg-[#165FE3] px-6 py-2.5 font-medium text-white-A700 transition-colors hover:bg-[#1942B8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#165FE3] disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-[#555555]"
            >
              {busy ? "Saving..." : submitLabel}
            </button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** A labelled field, so every dialog reads the same. */
export function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium text-[#333333]">
        {label}
      </label>
      {children}
      {hint ? <p className="text-xs text-[#666666]">{hint}</p> : null}
    </div>
  );
}

export const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-[#1B4ED7] focus:outline-none focus:ring-1 focus:ring-[#1B4ED7] disabled:cursor-not-allowed disabled:bg-gray-50";
